import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Chip,
  Button,
  IconButton,
  Card,
  CardContent,
  CardActions,
  Grid,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  TextField,
  Dialog,
  Alert,
  LinearProgress,
  CircularProgress,
  Stack,
  MenuItem,
  Select,
  InputAdornment,
  Zoom,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import AssignmentIcon from '@mui/icons-material/Assignment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BadgeIcon from '@mui/icons-material/Badge';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import GroupsIcon from '@mui/icons-material/Groups';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SearchIcon from '@mui/icons-material/Search';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import useFeedbackModal from '../../hooks/useFeedbackModal';
import { colors } from '../../theme/colors';
import EditarNotaAutorizacion from './EditarNotaAutorizacion';
import { useNotasAutorizacionAdmin } from './hooks/useNotasAutorizacionAdmin';
import { getCursos } from '../../services/cursos.service';

// ---------------------------------------------------------------------------
// HELPERS & CONFIG
// ---------------------------------------------------------------------------

// Convierte "Apellido, Nombre" -> "Nombre Apellido"
const formatCoordName = (referente) => {
  if (!referente) return '—';
  const partes = referente.split(',').map((p) => p.trim());
  if (partes.length === 2) {
    return `${partes[1]} ${partes[0]}`;
  }
  return referente;
};

const formatDate = (isoOrStr) => {
  if (!isoOrStr) return '—';
  const d = new Date(isoOrStr);
  if (isNaN(d.getTime())) return isoOrStr;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const ESTADO_CONFIG = {
  PCARGA: {
    label: 'Pendiente Suba',
    badgeText: 'Pendiente Suba',
    color: 'warning',
    dotColor: colors.CBA_Orange || '#F0AD4E',
    badgeBg: '#FFFBEB',
    badgeBorder: '#FDE68A',
    badgeColor: '#B45309',
    icon: <AccessTimeIcon sx={{ fontSize: 14 }} />,
    tooltip: 'En espera de que el referente suba la nota.',
  },
  PREV: {
    label: 'Pendientes Revisión',
    badgeText: 'Pendiente Revisión',
    color: 'info',
    dotColor: colors.CBA_Blue || '#009ada',
    badgeBg: '#EFF6FF',
    badgeBorder: '#BFDBFE',
    badgeColor: '#1D4ED8',
    icon: <AccessTimeIcon sx={{ fontSize: 14 }} />,
    tooltip: 'El equipo de administración debe revisar y validar la nota.',
  },
  PEND: {
    label: 'Pendientes Revisión',
    badgeText: 'Pendiente Revisión',
    color: 'info',
    dotColor: colors.CBA_Blue || '#009ada',
    badgeBg: '#EFF6FF',
    badgeBorder: '#BFDBFE',
    badgeColor: '#1D4ED8',
    icon: <AccessTimeIcon sx={{ fontSize: 14 }} />,
    tooltip: 'El equipo de administración debe revisar y validar la nota.',
  },
  AUT: {
    label: 'Autorizada',
    badgeText: 'Autorizada',
    color: 'success',
    dotColor: colors.CBA_Green || '#5CB85C',
    badgeBg: '#F0FDF4',
    badgeBorder: '#BBF7D0',
    badgeColor: '#15803D',
    icon: <CheckCircleIcon sx={{ fontSize: 14 }} />,
    tooltip: 'Nota aprobada y cursos autorizados para su gestión.',
  },
  REC: {
    label: 'Rechazada',
    badgeText: 'Rechazada',
    color: 'error',
    dotColor: colors.CBA_Error || '#E2464C',
    badgeBg: '#FEF2F2',
    badgeBorder: '#FECACA',
    badgeColor: '#B91C1C',
    icon: <CancelIcon sx={{ fontSize: 14 }} />,
    tooltip: 'Nota rechazada con observaciones para su corrección.',
  },
};

// ---------------------------------------------------------------------------
// ADAPTER DATA
// ---------------------------------------------------------------------------
const adaptarNotaAlFrontend = (backendNota) => {
  const cambiosEstados = backendNota.cambios_estados || [];

  let rawEstado = null;
  if (cambiosEstados.length > 0) {
    const activo = cambiosEstados.find((ce) => ce.fecha_hora_hasta === null);
    rawEstado = activo ? activo.estado_nota_autorizacion : cambiosEstados[cambiosEstados.length - 1].estado_nota_autorizacion;
  } else {
    rawEstado = backendNota.estado_nota_autorizacion_cod;
  }

  const mapEstado = (cod) => {
    if (cod === 'PCARG' || cod === 'PCARGA') return 'PCARGA';
    if (cod === 'PEND') return 'PREV';
    if (cod === 'EC' || cod === 'AUT') return 'AUT';
    if (cod === 'REC') return 'REC';
    return cod || 'PCARGA';
  };

  const getEstadoFecha = (cod) => {
    const ce = cambiosEstados.find((e) => e.estado_nota_autorizacion === cod);
    return ce ? ce.fecha_hora_desde : null;
  };

  const fechaGeneracion = getEstadoFecha('PCARGA') || getEstadoFecha('PCARG') || backendNota.fecha_hora_desde || backendNota.createdAt || null;
  const fechaSuba = getEstadoFecha('PEND') || getEstadoFecha('PREV') || backendNota.fecha_suba || null;
  const fechaAutorizacion = getEstadoFecha('AUT') || getEstadoFecha('EC') || null;
  const fechaRechazo = getEstadoFecha('REC') || null;
  const estado = mapEstado(rawEstado);
  const cambioRec = cambiosEstados.find((e) => e.estado_nota_autorizacion === 'REC');
  const motivoRechazo = estado === 'REC' ? (cambioRec?.comentario || backendNota.motivo_rechazo || null) : null;

  const coordinador = backendNota.detalle_coordinador || {};
  const referente = backendNota.detalle_usuario_referente?.detalle_persona || {};
  const autorizador = backendNota.detalle_autorizador_nuevo || {};
  const persAutorizador = autorizador.detalle_persona || {};

  const referenteNombre = `${referente.nombre || ''} ${referente.apellido || ''}`.trim() || 'Sin asignar';
  const coordNombre = coordinador.nombre && coordinador.apellido
    ? `${coordinador.nombre} ${coordinador.apellido}`.trim()
    : formatCoordName(referenteNombre);

  return {
    id: backendNota.id,
    estado,
    fecha: fechaGeneracion,
    fechaCarga: fechaGeneracion,
    fechaSuba,
    fechaAutorizacion,
    fechaRechazo,
    referente: referenteNombre,
    coordNombre,
    area: backendNota.detalle_area?.nombre || backendNota.area_cod,
    motivoRechazo,
    datos: {
      area: backendNota.detalle_area?.nombre || backendNota.area_cod,
      cursos: [...(backendNota.cursos || [])].sort((a, b) =>
        (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' })
      ),
      coordinador: {
        cuil: coordinador.cuil,
        nombre: coordinador.nombre || '',
        apellido: coordinador.apellido || '',
        mail: coordinador.mail || coordinador.email || '',
        celular: coordinador.celular || coordinador.telefono || '',
      },
      autorizador: {
        cuil: persAutorizador.cuil,
        nombre: persAutorizador.nombre || '',
        apellido: persAutorizador.apellido || '',
        descripcionCargo: autorizador.descripcion_cargo || '',
        area: backendNota.detalle_area?.nombre || '',
        correo: persAutorizador.mail || persAutorizador.email || persAutorizador.correo || '',
        celular: persAutorizador.celular || '',
      },
      publicoObjetivo: backendNota.publico_general || '',
      ventajas: backendNota.ventajas || '',
      tutores: (backendNota.tutores_nuevo || [])
        .map((t) => {
          const pers = t.detalle_persona || {};
          const rolNombre = t.detalle_rol_tutor?.nombre || (t.rol_tutor_cod === 'CPE' ? 'Con permiso de edición' : t.rol_tutor_cod === 'SPE' ? 'Sin permiso de edición' : t.rol_tutor_cod || '—');
          return {
            cuil: pers.cuil || t.persona_cuil,
            nombre: pers.nombre || '',
            apellido: pers.apellido || '',
            mail: pers.mail || pers.email || pers.correo || '',
            celular: pers.celular || '',
            rol: rolNombre,
            rolCod: t.rol_tutor_cod || (rolNombre?.toLowerCase().includes('con permiso') ? 'CPE' : 'SPE'),
          };
        })
        .sort((a, b) =>
          (String(a.cuil || '')).replace(/\D/g, '').localeCompare(String(b.cuil || '').replace(/\D/g, ''), undefined, { numeric: true })
        ),
      sga: (backendNota.accesos_victorius || [])
        .map((a) => {
          const pers = a.detalle_persona || {};
          return {
            cuil: pers.cuil || a.persona_cuil,
            nombre: pers.nombre || '',
            apellido: pers.apellido || '',
            mail: pers.mail || pers.email || pers.correo || '',
            celular: pers.celular || '',
          };
        })
        .sort((a, b) =>
          (String(a.cuil || '')).replace(/\D/g, '').localeCompare(String(b.cuil || '').replace(/\D/g, ''), undefined, { numeric: true })
        ),
      comunicacion: backendNota.comunicacion || persAutorizador.mail || coordinador.mail || referente.mail || '',
    },
    rutaArchivoLocal: backendNota.ruta_archivo_local || null,
    pdfUrl: backendNota.ruta_archivo_local
      ? `/${backendNota.ruta_archivo_local.replace(/\\/g, '/').startsWith('/') ? backendNota.ruta_archivo_local.replace(/\\/g, '/').slice(1) : backendNota.ruta_archivo_local.replace(/\\/g, '/')}`
      : null,
  };
};

// ---------------------------------------------------------------------------
// Sub-component: PdfViewer — Visualizador de PDF seguro para desarrollo
// ---------------------------------------------------------------------------
const PdfViewer = ({ pdfUrl }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(false);
  const isDev = process.env.NODE_ENV === 'development';

  useEffect(() => {
    if (!isDev) {
      setBlobUrl(pdfUrl);
      return;
    }

    let objectUrl = null;
    if (pdfUrl) {
      fetch(pdfUrl)
        .then((res) => {
          if (!res.ok) throw new Error('Network response was not ok');
          return res.blob();
        })
        .then((blob) => {
          objectUrl = URL.createObjectURL(blob);
          setBlobUrl(objectUrl);
        })
        .catch((err) => {
          console.error('Error loading PDF via fetch:', err);
          setError(true);
        });
    }
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [pdfUrl, isDev]);

  if (error) {
    return <Typography color="error" sx={{ p: 2, textAlign: 'center' }}>Error al cargar el PDF. Intente nuevamente.</Typography>;
  }

  if (!blobUrl) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      component="iframe"
      src={blobUrl}
      title="PDF de Nota de Autorización"
      sx={{ flex: 1, border: 'none', width: '100%' }}
    />
  );
};

// ---------------------------------------------------------------------------
// Sub-component: InfoItem — fila de dato individual en el panel de revisión
// Formato unificado: Etiqueta: Valor (mismo formato de la tarjeta de nota)
// ---------------------------------------------------------------------------
const InfoItem = ({ label, value, fallback = '—' }) => {
  const displayVal = value !== undefined && value !== null && String(value).trim() !== '' ? value : fallback;
  return (
    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, lineHeight: 1.5 }}>
      {label}:{' '}
      <span style={{ color: '#000', fontWeight: 550 }}>
        {displayVal}
      </span>
    </Typography>
  );
};

// ---------------------------------------------------------------------------
// Component: ReviewPanel (Vista dividida de revisión individual)
// ---------------------------------------------------------------------------
const ReviewPanel = ({ nota, onBack, onConfirmAsync, onReject, isRejecting = false, onDelete, onEdit }) => {
  const [expandedSections, setExpandedSections] = useState([
    'cursos',
    'publicoObjetivo',
    'ventajas',
    'coordinador',
    'tutores',
    'sga',
    'autorizador',
  ]);
  const [isConfirming, setIsConfirming] = useState(false);
  const [existingCursos, setExistingCursos] = useState([]);
  const [loadingExistingCursos, setLoadingExistingCursos] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const fetchExistingCursos = async () => {
      try {
        setLoadingExistingCursos(true);
        const data = await getCursos();
        if (isMounted) {
          setExistingCursos(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.warn('Error al obtener cursos existentes para validación:', error);
        if (isMounted) {
          setExistingCursos([]);
        }
      } finally {
        if (isMounted) {
          setLoadingExistingCursos(false);
        }
      }
    };

    fetchExistingCursos();
    return () => {
      isMounted = false;
    };
  }, []);

  // Copia editable de los cursos de la nota para modificar el código (ordenados alfabéticamente)
  const [cursos, setCursos] = useState(() =>
    [...(nota.datos.cursos || [])]
      .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' }))
      .map((c) => ({
        ...c,
        codOriginal: c.cod || c.cod_curso || '',
        codNuevo: c.cod || c.cod_curso || '',
      }))
  );

  const handleAccordion = (panel) => {
    setExpandedSections((prev) =>
      prev.includes(panel) ? prev.filter((p) => p !== panel) : [...prev, panel]
    );
  };

  const isExp = (panel) => expandedSections.includes(panel);

  const handleCursoCodChange = (index, newCod) => {
    const formattedCod = (newCod || '').replace(/\s+/g, '').toUpperCase().slice(0, 15);
    setCursos((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], codNuevo: formattedCod };
      return next;
    });
  };

  const getCursoCodeError = useCallback(
    (c, i) => {
      const origCod = (c.codOriginal || '').trim().toUpperCase();
      // Cursos con código definitivo ya asignado (no empieza con TMP-) no son editables
      if (origCod && !origCod.startsWith('TMP-')) {
        return null;
      }

      const cleanCod = (c.codNuevo || '').replace(/\s+/g, '').toUpperCase();
      if (!cleanCod) {
        return 'El código no puede estar vacío';
      }
      if (cleanCod.startsWith('TMP-')) {
        return '⚠️ Reasignación obligatoria: debe asignar un código definitivo ("C-" o "EXT-")';
      }
      const isFormatValid = cleanCod.length <= 15 && (cleanCod.startsWith('C-') || cleanCod.startsWith('EXT-'));
      if (!isFormatValid) {
        return 'Debe iniciar con "C-" o "EXT-" (máx. 15 caracteres)';
      }

      // 1. Validar colisión contra los cursos existentes en la base de datos
      const existsInDb = existingCursos.some((ec) => {
        const ecCod = (ec.cod || ec.cod_curso || '').trim().toUpperCase();
        const originalCod = (c.codOriginal || '').trim().toUpperCase();
        return ecCod === cleanCod && ecCod !== originalCod;
      });
      if (existsInDb) {
        return `El código "${cleanCod}" ya existe en el sistema. Elija otro código.`;
      }

      // 2. Validar duplicados dentro de la misma nota
      const duplicateInNote = cursos.some((otherC, otherIdx) => {
        if (otherIdx === i) return false;
        const otherCod = (otherC.codNuevo || '').replace(/\s+/g, '').toUpperCase();
        return otherCod !== '' && otherCod === cleanCod;
      });
      if (duplicateInNote) {
        return `El código "${cleanCod}" está repetido en otro curso de esta nota.`;
      }

      return null;
    },
    [existingCursos, cursos]
  );

  const hasPdf = Boolean(nota.pdfUrl || nota.rutaArchivoLocal || nota.ruta_archivo_local);
  const hasCodeErrors = cursos.some((c, i) => Boolean(getCursoCodeError(c, i)));
  const isCursosInvalid = cursos.length === 0 || hasCodeErrors;
  const isConfirmDisabled = isConfirming || isRejecting || isCursosInvalid || !hasPdf || loadingExistingCursos;
  const { showConfirm, showInfo, showWarning, showError } = useFeedbackModal();

  const handleConfirmSubmit = async () => {
    if (nota.estado === 'AUT') {
      await showInfo({
        title: 'Nota ya autorizada',
        description: 'Esta nota de autorización ya fue autorizada previamente.',
      });
      return;
    }

    if (!hasPdf) {
      await showWarning({
        title: 'PDF Requerido',
        description: 'No se puede confirmar la nota porque no se ha subido el archivo PDF firmado (ruta_archivo_local).',
      });
      return;
    }

    const invalidCurso = cursos.find((c, i) => Boolean(getCursoCodeError(c, i)));
    if (invalidCurso) {
      const errorMsg = getCursoCodeError(invalidCurso, cursos.indexOf(invalidCurso));
      await showError({
        title: 'Código de Curso Inválido o Duplicado',
        description: errorMsg,
      });
      return;
    }

    const payloadCursos = cursos
      .filter((c) => c.codOriginal !== c.codNuevo)
      .map((c) => ({ codOriginal: c.codOriginal, codNuevo: c.codNuevo }));

    const isConfirmed = await showConfirm({
      title: '¿Está seguro que desea autorizar la nota?',
      confirmText: 'AUTORIZAR',
      cancelText: 'CANCELAR',
      confirmButtonColor: '#00a651',
      confirmButtonHoverColor: '#008e45',
      confirmButtonActiveColor: '#007a3b',
      ringBadge: true,
      type: 'question',
    });

    if (isConfirmed) {
      setIsConfirming(true);
      await onConfirmAsync(payloadCursos);
      setIsConfirming(false);
    }
  };

  const cfg = ESTADO_CONFIG[nota.estado] || ESTADO_CONFIG.PCARGA;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100vw',
        bgcolor: '#f0f2f5',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 1250,
      }}
    >
      {/* ── Top bar ── */}
      <Paper
        square
        elevation={2}
        sx={{
          px: 3,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'common.white',
          borderBottom: '1px solid',
          borderColor: 'divider',
          zIndex: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Volver
          </Button>

          <Typography variant="h6" fontWeight="bold" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Nota #{nota.id}
          </Typography>

          <Chip
            size="small"
            label={cfg.label}
            color={cfg.color || 'default'}
            icon={cfg.icon}
            sx={{ fontWeight: 'bold' }}
          />

          {cfg.tooltip && (
            <Tooltip title={cfg.tooltip} arrow placement="top">
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: cfg.dotColor,
                  transition: 'transform 0.15s ease-in-out, opacity 0.15s ease-in-out',
                  '&:hover': {
                    opacity: 0.8,
                    transform: 'scale(1.15)',
                  },
                }}
              >
                <HelpOutlineIcon sx={{ fontSize: 18 }} />
              </Box>
            </Tooltip>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {nota.estado === 'PCARGA' && onEdit && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<EditIcon />}
              onClick={onEdit}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Editar Nota
            </Button>
          )}

          {nota.estado === 'PCARGA' && onDelete && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={onDelete}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Eliminar
            </Button>
          )}

          {nota.estado !== 'AUT' && nota.estado !== 'REC' && (
            <Tooltip
              title={
                nota.estado === 'PCARGA'
                  ? 'No se puede rechazar una nota en estado Pendiente Suba (debe esperar a que el referente suba el documento).'
                  : ''
              }
              arrow
            >
              <span>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={isRejecting ? <CircularProgress size={16} color="inherit" /> : <CancelIcon />}
                  onClick={onReject}
                  disabled={isRejecting || isConfirming || nota.estado === 'PCARGA'}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  {isRejecting ? 'Rechazando...' : 'Rechazar'}
                </Button>
              </span>
            </Tooltip>
          )}

          {nota.estado !== 'AUT' && (
            <Tooltip
              title={
                !hasPdf
                  ? 'No se puede confirmar: falta subir el PDF firmado (ruta_archivo_local).'
                  : loadingExistingCursos
                    ? 'Cargando y validando códigos de cursos existentes...'
                    : isCursosInvalid
                      ? 'No se puede confirmar: revise los códigos de cursos señalados en rojo (deben ser válidos, únicos y no repetirse en el sistema).'
                      : ''
              }
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={isConfirming ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
                  onClick={handleConfirmSubmit}
                  disabled={isConfirmDisabled}
                  sx={{ fontWeight: 'bold', px: 3, borderRadius: 2, textTransform: 'none' }}
                >
                  {isConfirming ? 'Autorizando nota...' : 'Autorizar'}
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Paper>

      {/* ── Main body split panel ── */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', flexDirection: { xs: 'column', md: 'row' } }}>
        {/* LEFT: Metadata & editable form */}
        <Box
          sx={{
            width: { xs: '100%', md: '450px', lg: '500px' },
            height: { xs: '50%', md: '100%' },
            overflowY: 'auto',
            p: 2.5,
            borderRight: '1px solid',
            borderColor: 'divider',
            bgcolor: 'common.white',
          }}
        >
          {/* 1. Header Card: Datos de la Solicitud */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
            <Typography variant="subtitle2" color="primary" fontWeight="bold" gutterBottom>
              DATOS DE LA NOTA
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" display="block">Área Responsable</Typography>
                <Typography variant="subtitle2" color="text.primary" fontWeight={700} sx={{ mb: 0.5 }}>{nota.area}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" display="block">Referente</Typography>
                <Typography variant="body2" color="text.primary">{nota.referente}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" display="block">Fecha de Generación</Typography>
                <Typography variant="body2" color="text.primary">{formatDate(nota.fecha)}</Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* 2. Cursos */}
          <Accordion expanded={isExp('cursos')} onChange={() => handleAccordion('cursos')} disableGutters elevation={0} variant="outlined" sx={{ mb: 1, borderRadius: '8px !important', overflow: 'hidden' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'grey.50', minHeight: 44 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SchoolIcon fontSize="small" color="primary" />
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Cursos ({cursos.length})
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {cursos.map((c, i) => {
                const codeError = getCursoCodeError(c, i);
                const origCodUpper = (c.codOriginal || '').trim().toUpperCase();
                const isTempCode = Boolean(origCodUpper.startsWith('TMP-'));
                const isAlreadyAssigned = Boolean(origCodUpper && !isTempCode);

                return (
                  <Box key={`curso-${i}`} sx={{ px: 2, py: 2, borderTop: i > 0 ? '1px solid' : 'none', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                      <Box>
                        <Typography variant="subtitle2" color="text.primary" fontWeight={700} sx={{ mb: 0.5 }}>
                          {c.nombre}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Cupo:{' '}
                          <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                            {c.cupo ?? '—'}
                          </Box>
                          <Box component="span" sx={{ mx: 1, color: 'text.secondary' }}>
                            •
                          </Box>
                          Cantidad de horas:{' '}
                          <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                            {c.cantidad_horas ?? '—'}
                          </Box>
                          <Box component="span" sx={{ mx: 1, color: 'text.secondary' }}>
                            •
                          </Box>
                          Plataforma de dictado:{' '}
                          <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                            {c.plataforma_dictado ?? '—'}
                          </Box>
                        </Typography>
                      </Box>
                      {(nota.estado === 'PREV' || nota.estado === 'PEND' || nota.estado === 'PCARGA') && isTempCode && (
                        <Tooltip
                          title="Reasignación obligatoria de código temporal"
                          placement="left"
                          arrow
                        >
                          <IconButton sx={{ color: 'warning.main' }}>
                            <HelpOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>

                    {nota.estado === 'PREV' || nota.estado === 'PEND' || nota.estado === 'PCARGA' ? (
                      <Box sx={{ mt: 1.5 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 500 }}>
                          Código Corto del Curso
                        </Typography>
                        <TextField
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={c.codNuevo}
                          onChange={(e) => handleCursoCodChange(i, e.target.value)}
                          inputProps={{
                            maxLength: 15,
                            'aria-label': 'Código Corto del Curso',
                          }}
                          disabled={isAlreadyAssigned}
                          error={Boolean(codeError)}
                          helperText={codeError || undefined}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px',
                              bgcolor: '#ffffff',
                              '& fieldset': {
                                borderColor: '#d0d7de',
                              },
                              '&:hover fieldset': {
                                borderColor: isAlreadyAssigned ? '#d0d7de' : '#94a3b8',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: isAlreadyAssigned ? '#d0d7de' : 'primary.main',
                              },
                              '&.Mui-disabled': {
                                bgcolor: '#ffffff',
                                '& fieldset': {
                                  borderColor: '#d0d7de',
                                },
                                '& input': {
                                  WebkitTextFillColor: '#1e293b',
                                  color: '#1e293b',
                                  cursor: 'default',
                                },
                              },
                            },
                            '& .MuiOutlinedInput-input': {
                              py: 1,
                              px: 1.5,
                              fontSize: '0.925rem',
                              color: '#1e293b',
                            },
                          }}
                          InputProps={{
                            endAdornment: loadingExistingCursos ? (
                              <InputAdornment position="end">
                                <CircularProgress size={16} />
                              </InputAdornment>
                            ) : null,
                          }}
                        />
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.primary" sx={{ display: 'inline-block', mt: 0.5, px: 1, py: 0.2, bgcolor: 'action.selected', borderRadius: 1 }}>
                        Cod: <strong>{c.codNuevo}</strong>
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </AccordionDetails>
          </Accordion>

          {/* 3. Público Objetivo */}
          {nota.datos?.publicoObjetivo && (
            <Accordion expanded={isExp('publicoObjetivo')} onChange={() => handleAccordion('publicoObjetivo')} disableGutters elevation={0} variant="outlined" sx={{ mb: 1, borderRadius: '8px !important', overflow: 'hidden' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'grey.50', minHeight: 44 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GroupsIcon fontSize="small" color="primary" />
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Público Objetivo
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2 }}>
                <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                  {nota.datos.publicoObjetivo}
                </Typography>
              </AccordionDetails>
            </Accordion>
          )}

          {/* 4. Ventajas / Beneficios */}
          {nota.datos?.ventajas && (
            <Accordion expanded={isExp('ventajas')} onChange={() => handleAccordion('ventajas')} disableGutters elevation={0} variant="outlined" sx={{ mb: 1, borderRadius: '8px !important', overflow: 'hidden' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'grey.50', minHeight: 44 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ThumbUpAltIcon fontSize="small" color="primary" />
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Ventajas
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2 }}>
                <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                  {nota.datos.ventajas}
                </Typography>
              </AccordionDetails>
            </Accordion>
          )}

          {/* 5. Coordinador/a (Gestión de Accesos) */}
          <Accordion expanded={isExp('coordinador')} onChange={() => handleAccordion('coordinador')} disableGutters elevation={0} variant="outlined" sx={{ mb: 1, borderRadius: '8px !important', overflow: 'hidden' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'grey.50', minHeight: 44 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon fontSize="small" color="primary" />
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  2. Coordinación (Gestión de Accesos)
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2 }}>
              <InfoItem label="CUIL" value={nota.datos.coordinador.cuil} />
              <InfoItem label="Nombre" value={`${nota.datos.coordinador.nombre || ''} ${nota.datos.coordinador.apellido || ''}`.trim()} />
              <InfoItem label="Correo" value={nota.datos.coordinador.mail} />
              <InfoItem label="Celular" value={nota.datos.coordinador.celular} />
            </AccordionDetails>
          </Accordion>

          {/* 6. Tutores */}
          <Accordion expanded={isExp('tutores')} onChange={() => handleAccordion('tutores')} disableGutters elevation={0} variant="outlined" sx={{ mb: 1, borderRadius: '8px !important', overflow: 'hidden' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'grey.50', minHeight: 44 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SupervisorAccountIcon fontSize="small" color="primary" />
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  3. Tutores ({(nota.datos.tutores || []).length})
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2 }}>
              {(() => {
                const allTutores = nota.datos.tutores || [];
                if (allTutores.length === 0) {
                  return (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No se han asignado tutores.
                    </Typography>
                  );
                }

                const isCPE = (t) => {
                  const cod = (t.rolCod || t.rol_tutor_cod || '').toUpperCase();
                  if (cod === 'CPE') return true;
                  if (cod === 'SPE') return false;
                  return (t.rol || '').toLowerCase().includes('con permiso');
                };

                const isSPE = (t) => {
                  const cod = (t.rolCod || t.rol_tutor_cod || '').toUpperCase();
                  if (cod === 'SPE') return true;
                  if (cod === 'CPE') return false;
                  return (t.rol || '').toLowerCase().includes('sin permiso');
                };

                const tutoresConPermiso = allTutores.filter(isCPE);
                const tutoresSinPermiso = allTutores.filter(isSPE);
                const tutoresOtros = allTutores.filter((t) => !isCPE(t) && !isSPE(t));

                const renderTutorItem = (p, idx, list) => {
                  const nombreCompleto = `${p.nombre || ''} ${p.apellido || ''}`.trim();
                  return (
                    <Box key={p.cuil ? `tutor-${p.cuil}-${idx}` : `tutor-${idx}`} sx={{ mb: idx < list.length - 1 ? 2 : 0 }}>
                      <InfoItem label="CUIL" value={p.cuil} />
                      <InfoItem label="Nombre" value={nombreCompleto} />
                      <InfoItem label="Correo" value={p.mail} />
                      <InfoItem label="Celular" value={p.celular} />
                    </Box>
                  );
                };

                return (
                  <Box>
                    {tutoresConPermiso.length > 0 && (
                      <Box>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          color="text.secondary"
                          sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1.5 }}
                        >
                          TUTORES CON PERMISO DE EDICIÓN
                        </Typography>
                        {tutoresConPermiso.map((p, idx) => renderTutorItem(p, idx, tutoresConPermiso))}
                      </Box>
                    )}

                    {tutoresConPermiso.length > 0 && tutoresSinPermiso.length > 0 && (
                      <Divider sx={{ my: 2 }} />
                    )}

                    {tutoresSinPermiso.length > 0 && (
                      <Box>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          color="text.secondary"
                          sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1.5 }}
                        >
                          TUTORES SIN PERMISO DE EDICIÓN
                        </Typography>
                        {tutoresSinPermiso.map((p, idx) => renderTutorItem(p, idx, tutoresSinPermiso))}
                      </Box>
                    )}

                    {tutoresOtros.length > 0 && (
                      <Box sx={{ mt: (tutoresConPermiso.length > 0 || tutoresSinPermiso.length > 0) ? 2 : 0 }}>
                        {(tutoresConPermiso.length > 0 || tutoresSinPermiso.length > 0) && <Divider sx={{ my: 2 }} />}
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          color="text.secondary"
                          sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1.5 }}
                        >
                          OTROS TUTORES
                        </Typography>
                        {tutoresOtros.map((p, idx) => renderTutorItem(p, idx, tutoresOtros))}
                      </Box>
                    )}
                  </Box>
                );
              })()}
            </AccordionDetails>
          </Accordion>

          {/* 7. Acceso SGA (Victorius) */}
          <Accordion expanded={isExp('sga')} onChange={() => handleAccordion('sga')} disableGutters elevation={0} variant="outlined" sx={{ mb: 1, borderRadius: '8px !important', overflow: 'hidden' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'grey.50', minHeight: 44 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AdminPanelSettingsIcon fontSize="small" color="primary" />
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  4. Acceso al Sistema de Gestión Académica ({(nota.datos.sga || []).length})
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2 }}>
              {(nota.datos.sga || []).length > 0 ? (
                (nota.datos.sga || []).map((p, i, arr) => {
                  const nombreCompleto = `${p.nombre || ''} ${p.apellido || ''}`.trim();
                  return (
                    <Box
                      key={p.cuil ? `sga-${p.cuil}-${i}` : `sga-${i}`}
                      sx={{
                        mb: i < arr.length - 1 ? 2 : 0,
                        pb: i < arr.length - 1 ? 1.5 : 0,
                        borderBottom: i < arr.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider',
                      }}
                    >
                      <InfoItem label="CUIL" value={p.cuil} />
                      <InfoItem label="Nombre" value={nombreCompleto} />
                      <InfoItem label="Correo" value={p.mail} />
                      <InfoItem label="Celular" value={p.celular} />
                    </Box>
                  );
                })
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No se han asignado personas para acceso al SGA.
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>

          {/* 8. Comunicación */}
          <Paper variant="outlined" sx={{ mb: 1, p: 2, borderRadius: 2, bgcolor: '#fafafa' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <EmailIcon fontSize="small" color="primary" />
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                6. Comunicación (Atención a la ciudadanía)
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" display="block">Correo de Contacto</Typography>
            <Typography variant="body2" fontWeight={600} color="text.primary">
              {nota.datos?.comunicacion || 'No especificado'}
            </Typography>
          </Paper>

          {/* 9. Autorizador/a del Área (Firma) */}
          <Accordion expanded={isExp('autorizador')} onChange={() => handleAccordion('autorizador')} disableGutters elevation={0} variant="outlined" sx={{ mb: 1, borderRadius: '8px !important', overflow: 'hidden' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'grey.50', minHeight: 44 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BadgeIcon fontSize="small" color="primary" />
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Autorizador/a del Área (Firma)
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2 }}>
              <InfoItem label="CUIL" value={nota.datos.autorizador.cuil} />
              <InfoItem label="Nombre" value={`${nota.datos.autorizador.nombre || ''} ${nota.datos.autorizador.apellido || ''}`.trim()} />
              <InfoItem label="Cargo" value={nota.datos.autorizador.descripcionCargo} />
              <InfoItem label="Área" value={nota.datos.autorizador.area} />
              <InfoItem label="Correo" value={nota.datos.autorizador.correo} />
              <InfoItem label="Celular" value={nota.datos.autorizador.celular} />
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* RIGHT: PDF viewer */}
        <Box
          sx={{
            flex: 1,
            height: { xs: '50%', md: '100%' },
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#525659',
          }}
        >
          <Box sx={{ px: 2, py: 1, bgcolor: '#3c3f41', display: 'flex', alignItems: 'center', gap: 1 }}>
            <PictureAsPdfIcon sx={{ color: '#e57373', fontSize: 18 }} />
            <Typography variant="caption" sx={{ color: '#ccc', fontFamily: 'monospace' }}>
              nota-autorizacion-{nota.id}.pdf
            </Typography>
          </Box>

          {nota.pdfUrl ? (
            <PdfViewer pdfUrl={nota.pdfUrl} />
          ) : (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
              <PictureAsPdfIcon sx={{ fontSize: 80, color: 'rgba(255,255,255,0.15)' }} />
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', maxWidth: 340 }}>
                El PDF de la nota de autorización aún no está disponible. Se mostrará cuando el referente lo suba con la firma correspondiente
              </Typography>
              <Chip
                label="PDF Mock — No disponible aún"
                variant="outlined"
                sx={{ color: 'rgba(255,255,255,0.4)', borderColor: 'rgba(255,255,255,0.2)' }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main component: ConfirmarNotaAutorizacionAdmin
// ---------------------------------------------------------------------------
const ConfirmarNotaAutorizacionAdmin = () => {
  const { notas: rawNotas, loading, fetchNotas, confirmarNota, rechazarNota, eliminarNota } = useNotasAutorizacionAdmin();
  const { showDelete } = useFeedbackModal();

  const [reviewingNota, setReviewingNota] = useState(null);
  const [editingNotaId, setEditingNotaId] = useState(null);

  // Filtros
  const [filterEstado, setFilterEstado] = useState('TODAS');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterArea, setFilterArea] = useState('Todas las áreas');
  const [filterYear, setFilterYear] = useState(String(new Date().getFullYear()));

  // Ordenamiento
  const [sortByField, setSortByField] = useState('fecha');
  const [sortDesc, setSortDesc] = useState(true);

  // Reject dialog & loading
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [motivoError, setMotivoError] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);

  useEffect(() => {
    fetchNotas();
  }, [fetchNotas]);

  const notas = useMemo(
    () => rawNotas.map(adaptarNotaAlFrontend),
    [rawNotas]
  );

  const areasDisponibles = useMemo(() => {
    const areas = Array.from(new Set(notas.map((n) => n.area).filter(Boolean)));
    return areas.sort((a, b) => a.localeCompare(b));
  }, [notas]);

  const currentYear = new Date().getFullYear();
  const yearsDisponibles = useMemo(() => {
    const years = new Set([String(currentYear)]);
    notas.forEach((n) => {
      if (n.fecha) {
        const d = new Date(n.fecha);
        if (!isNaN(d.getFullYear())) {
          years.add(String(d.getFullYear()));
        }
      }
    });
    return Array.from(years).sort((a, b) => Number(b) - Number(a));
  }, [notas, currentYear]);

  const counts = useMemo(
    () => ({
      TODAS: notas.length,
      PCARGA: notas.filter((n) => n.estado === 'PCARGA').length,
      PREV: notas.filter((n) => n.estado === 'PREV' || n.estado === 'PEND').length,
      AUT: notas.filter((n) => n.estado === 'AUT').length,
      REC: notas.filter((n) => n.estado === 'REC').length,
    }),
    [notas]
  );

  const parseDateToMillis = (val) => {
    if (!val) return 0;
    const t = new Date(val).getTime();
    return isNaN(t) ? 0 : t;
  };

  const notasFiltradas = useMemo(() => {
    return notas
      .filter((n) => {
        // Filtro de Estado
        if (filterEstado !== 'TODAS') {
          if (filterEstado === 'PREV') {
            if (n.estado !== 'PREV' && n.estado !== 'PEND') return false;
          } else if (n.estado !== filterEstado) {
            return false;
          }
        }

        // Filtro de Área
        if (filterArea !== 'Todas las áreas' && n.area !== filterArea) {
          return false;
        }

        // Filtro de Año de Generación
        if (filterYear !== 'TODOS') {
          if (!n.fecha) return false;
          const d = new Date(n.fecha);
          if (String(d.getFullYear()) !== filterYear) return false;
        }

        // Buscador unificado: ID Nota, Referente o Curso
        if (searchQuery.trim() !== '') {
          const query = searchQuery.trim().toLowerCase();
          const matchId = n.id.toString().includes(query);
          const matchReferente = (n.referente || '').toLowerCase().includes(query);
          const matchCurso = (n.datos?.cursos || []).some((c) =>
            (c.nombre || '').toLowerCase().includes(query)
          );
          if (!matchId && !matchReferente && !matchCurso) return false;
        }

        return true;
      })
      .sort((a, b) => {
        const valA = a[sortByField];
        const valB = b[sortByField];

        const dateA = parseDateToMillis(valA);
        const dateB = parseDateToMillis(valB);

        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;

        const diff = dateA - dateB;
        return sortDesc ? -diff : diff;
      });
  }, [notas, filterEstado, filterArea, filterYear, searchQuery, sortByField, sortDesc]);

  const chips = [
    { key: 'TODAS', label: `Todas (${counts.TODAS})` },
    { key: 'PCARGA', label: `Pendientes Suba (${counts.PCARGA})` },
    { key: 'PREV', label: `Pendientes Revisión (${counts.PREV})` },
    { key: 'AUT', label: `Autorizadas (${counts.AUT})` },
    { key: 'REC', label: `Rechazadas (${counts.REC})` },
  ];

  const handleConfirmAsync = async (cursosActualizados) => {
    const ok = await confirmarNota(reviewingNota.id, cursosActualizados);
    if (ok) {
      setReviewingNota(null);
    }
  };

  const handleOpenReject = () => {
    if (reviewingNota?.estado === 'PCARGA') return;
    setMotivoRechazo('');
    setMotivoError('');
    setOpenRejectDialog(true);
  };

  const handleConfirmReject = async () => {
    if (!motivoRechazo.trim()) {
      setMotivoError('El motivo de rechazo es obligatorio.');
      return;
    }
    setOpenRejectDialog(false);
    setIsRejecting(true);

    try {
      const ok = await rechazarNota(reviewingNota.id, motivoRechazo.trim());
      if (ok) {
        setReviewingNota(null);
      }
    } finally {
      setIsRejecting(false);
    }
  };

  const handleDeleteNota = async (notaId) => {
    const isConfirmed = await showDelete({
      title: '¿Eliminar nota de autorización?',
      detail: {
        text: '¿Está seguro de que quiere eliminar la',
        highlight: `Nota #${notaId}?`,
        subtext: 'Esta acción desvinculará los cursos y eliminará los tutores y accesos asociados.',
      },
    });

    if (isConfirmed) {
      const ok = await eliminarNota(notaId);
      if (ok && reviewingNota?.id === notaId) {
        setReviewingNota(null);
      }
    }
  };

  // ── Modo Edición ──
  if (editingNotaId) {
    return (
      <EditarNotaAutorizacion
        notaId={editingNotaId}
        onCancel={() => setEditingNotaId(null)}
        onSuccess={() => {
          setEditingNotaId(null);
          if (reviewingNota) setReviewingNota(null);
          fetchNotas();
        }}
      />
    );
  }

  // ── Modo Revisión Dividida ──
  if (reviewingNota) {
    return (
      <>
        <ReviewPanel
          nota={reviewingNota}
          onBack={() => setReviewingNota(null)}
          onConfirmAsync={handleConfirmAsync}
          onReject={handleOpenReject}
          isRejecting={isRejecting}
          onDelete={() => handleDeleteNota(reviewingNota.id)}
          onEdit={() => setEditingNotaId(reviewingNota.id)}
        />

        {/* Reject Dialog */}
        <Dialog
          open={openRejectDialog}
          onClose={() => setOpenRejectDialog(false)}
          TransitionComponent={Zoom}
          PaperProps={{
            sx: {
              width: '100%',
              maxWidth: '490px',
              borderRadius: '26px',
              p: { xs: 2.5, sm: 3.5 },
              boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.25)',
              border: '1px solid #F1F5F9',
              backgroundColor: '#FFFFFF',
              overflow: 'hidden',
              m: 2,
            },
          }}
          BackdropProps={{
            sx: {
              backgroundColor: 'rgba(15, 23, 42, 0.60)',
              backdropFilter: 'blur(6px)',
            },
          }}
        >
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, mb: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                backgroundColor: '#FFF1F2',
                border: '1px solid rgba(254, 205, 211, 0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <CloseRoundedIcon sx={{ color: '#FF2D3B', fontSize: 22, fontWeight: 700 }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: '1.25rem', sm: '1.38rem' },
                fontWeight: 800,
                letterSpacing: '-0.02em',
                color: '#0F172A',
                fontFamily: '"Poppins", sans-serif',
                lineHeight: 1.25,
              }}
            >
              Rechazar nota de autorización
            </Typography>
          </Box>

          {/* Subtitle / Instruction */}
          <Typography
            variant="body2"
            sx={{
              color: '#64748B',
              fontSize: '0.92rem',
              fontWeight: 500,
              lineHeight: 1.5,
              mb: 2,
            }}
          >
            Indique el motivo por el cual la nota será rechazada.
          </Typography>

          {/* Motivo Input Area */}
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              handleConfirmReject();
            }}
          >
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Ingrese el motivo de rechazo..."
              value={motivoRechazo}
              onChange={(e) => {
                setMotivoRechazo(e.target.value);
                setMotivoError('');
              }}
              error={!!motivoError}
              helperText={motivoError}
              autoFocus
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '16px',
                  backgroundColor: '#FFFFFF',
                  fontSize: '0.9rem',
                  p: 1.75,
                  '& fieldset': {
                    borderColor: '#E2E8F0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#CBD5E1',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#F87171',
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 0 0 3px rgba(255, 45, 59, 0.12)',
                  },
                },
                '& .MuiInputBase-input': {
                  lineHeight: 1.6,
                  color: '#1E293B',
                  '&::placeholder': {
                    color: '#94A3B8',
                    opacity: 1,
                  },
                },
              }}
            />

            {/* Modal Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.5, mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => setOpenRejectDialog(false)}
                sx={{
                  minHeight: '42px',
                  px: 3,
                  borderRadius: '12px',
                  fontSize: '0.8125rem',
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  backgroundColor: '#FFFFFF',
                  borderColor: 'rgba(226, 232, 240, 0.9)',
                  color: '#475569',
                  '&:hover': {
                    backgroundColor: '#F8FAFC',
                    color: '#1E293B',
                    borderColor: '#CBD5E1',
                  },
                  '&:active': {
                    transform: 'scale(0.98)',
                  },
                }}
                disabled={isRejecting}
              >
                CANCELAR
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={isRejecting}
                startIcon={isRejecting ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{
                  minHeight: '42px',
                  px: 3.5,
                  borderRadius: '12px',
                  fontSize: '0.8125rem',
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  backgroundColor: '#FF2D3B',
                  color: '#FFFFFF',
                  boxShadow: '0 2px 8px rgba(255, 45, 59, 0.25)',
                  '&:hover': {
                    backgroundColor: '#E8202E',
                    boxShadow: '0 4px 12px rgba(255, 45, 59, 0.35)',
                  },
                  '&:active': {
                    transform: 'scale(0.98)',
                  },
                }}
              >
                {isRejecting ? 'RECHAZANDO...' : 'RECHAZAR'}
              </Button>
            </Box>
          </Box>
        </Dialog>
      </>
    );
  }

  // ── Vista Principal con Tarjetas ──
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Encabezado */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'flex-start' },
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Box>
          <Typography
            variant="h4"
            fontWeight={800}
            color="text.primary"
            sx={{
              fontFamily: 'Poppins, sans-serif',
              letterSpacing: '-0.02em',
              mb: 0.5,
            }}
          >
            Gestionar Notas de Autorización
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Revise las notas de autorización cargadas. Compare los datos con el PDF firmado y confírmela o rechácela.
          </Typography>
        </Box>


      </Box>

      {/* Fila de Filtros Superiores (Buscador, Área, Año de Generación) */}
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2.5,
          bgcolor: 'common.white',
          borderColor: 'grey.300',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 2.5,
          boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
        }}
      >
        {/* Buscador unificado: ID Nota, Referente o Curso */}
        <TextField
          size="small"
          placeholder="Buscar por ID Nota, Referente o Curso"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: 1,
            minWidth: { xs: 0, md: 300 },
            width: { xs: '100%', md: 'auto' },
            bgcolor: 'common.white',
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            },
          }}
        />

        {/* Área */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', md: 'auto' } }}>
          <Typography variant="body2" fontWeight={550} color="text.primary" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
            Área:
          </Typography>
          <Select
            size="small"
            value={filterArea}
            onChange={(e) => setFilterArea(e.target.value)}
            sx={{
              flex: { xs: 1, md: 'initial' },
              width: { xs: '100%', md: 'auto' },
              minWidth: { xs: 0, md: 220 },
              bgcolor: 'common.white',
              borderRadius: 2,
            }}
          >
            <MenuItem value="Todas las áreas">Todas las áreas</MenuItem>
            {areasDisponibles.map((area) => (
              <MenuItem key={area} value={area}>
                {area}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* Año de Generación */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', md: 'auto' } }}>
          <Typography variant="body2" fontWeight={550} color="text.primary" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
            Año de Generación:
          </Typography>
          <Select
            size="small"
            value={filterYear}
            onChange={(e) => setFilterYear(e.target.value)}
            sx={{
              flex: { xs: 1, md: 'initial' },
              width: { xs: '100%', md: 'auto' },
              minWidth: { xs: 0, md: 160 },
              bgcolor: 'common.white',
              borderRadius: 2,
            }}
          >
            <MenuItem value="TODOS">Todos los años</MenuItem>
            {yearsDisponibles.map((yr) => (
              <MenuItem key={yr} value={yr}>
                {yr}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Paper>

      {/* Chips de Estado + Ordenamiento */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
        }}
      >
        {/* Chips de estado */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {chips.map(({ key, label }) => {
            const active = filterEstado === key;
            return (
              <Chip
                key={key}
                label={label}
                clickable
                onClick={() => setFilterEstado(key)}
                sx={{
                  borderRadius: '20px',
                  px: 0.5,
                  py: 2,
                  fontWeight: active ? 600 : 500,
                  fontSize: '0.85rem',
                  bgcolor: active ? 'primary.main' : 'common.white',
                  color: active ? 'common.white' : 'text.primary',
                  border: '1px solid',
                  borderColor: active ? 'primary.main' : 'grey.300',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: active ? 'primary.dark' : 'grey.100',
                    borderColor: 'primary.main',
                    color: active ? 'common.white' : 'primary.main',
                  },
                }}
              />
            );
          })}
        </Box>

        {/* Control de Orden */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 0.75,
              borderRadius: '20px',
              bgcolor: 'common.white',
              border: '1px solid',
              borderColor: 'grey.300',
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1, whiteSpace: 'nowrap' }}>
              Ordenar por:
            </Typography>
            <Select
              variant="standard"
              disableUnderline
              size="small"
              value={sortByField}
              onChange={(e) => setSortByField(e.target.value)}
              sx={{
                fontWeight: 600,
                fontSize: '0.875rem',
                color: 'text.primary',
                cursor: 'pointer',
              }}
            >
              <MenuItem value="fecha">Fecha de Generación</MenuItem>
              <MenuItem value="fechaSuba">Fecha de Suba</MenuItem>
              <MenuItem value="fechaAutorizacion">Fecha de Autorización</MenuItem>
              <MenuItem value="fechaRechazo">Fecha de Rechazo</MenuItem>
            </Select>
          </Box>

          <Tooltip
            title={
              sortDesc
                ? 'Orden descendente (más recientes primero). Clic para cambiar a ascendente.'
                : 'Orden ascendente (más antiguas primero). Clic para cambiar a descendente.'
            }
            arrow
          >
            <IconButton
              onClick={() => setSortDesc(!sortDesc)}
              sx={{
                p: 1,
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: 2,
                bgcolor: 'common.white',
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              <SwapVertIcon
                sx={{
                  fontSize: 20,
                  transform: sortDesc ? 'none' : 'rotate(180deg)',
                  transition: 'transform 0.2s ease',
                  color: 'text.secondary',
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Grid de Tarjetas */}
      {loading ? (
        <Box sx={{ width: '100%', mt: 4 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
            Cargando notas de autorización...
          </Typography>
        </Box>
      ) : notasFiltradas.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 8,
            textAlign: 'center',
            borderRadius: 3,
            borderColor: 'grey.300',
            bgcolor: 'common.white',
            boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
          }}
        >
          <AssignmentIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1.5, opacity: 0.5 }} />
          <Typography variant="body1" color="text.secondary">
            No hay notas en esta categoría.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {notasFiltradas.map((nota) => {
            const cfg = ESTADO_CONFIG[nota.estado] || ESTADO_CONFIG.PCARGA;
            return (
              <Grid item xs={12} sm={6} md={4} key={nota.id}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    borderColor: 'grey.200',
                    bgcolor: 'common.white',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    transition: 'box-shadow 0.2s, transform 0.2s',
                    '&:hover': {
                      boxShadow: '0 4px 14px rgba(0,0,0,0.09)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 2.5, pb: 1.5, flex: 1 }}>
                    {/* Header de la tarjeta: Área y Badge con Dot */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 1.5,
                        mb: 1.5,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        color="text.primary"
                        sx={{ lineHeight: 1.3 }}
                      >
                        {nota.area}
                      </Typography>

                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.8,
                          flexShrink: 0,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.8,
                            px: 1.25,
                            py: 0.4,
                            borderRadius: 4,
                            bgcolor: cfg.badgeBg,
                            border: '1px solid',
                            borderColor: cfg.badgeBorder,
                            color: cfg.badgeColor,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              bgcolor: cfg.dotColor,
                            }}
                          />
                          {cfg.badgeText}
                        </Box>

                        {cfg.tooltip && (
                          <Tooltip title={cfg.tooltip} arrow placement="top">
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                color: cfg.dotColor,
                                transition: 'transform 0.15s ease-in-out, opacity 0.15s ease-in-out',
                                '&:hover': {
                                  opacity: 0.8,
                                  transform: 'scale(1.15)',
                                },
                              }}
                            >
                              <HelpOutlineIcon sx={{ fontSize: 18 }} />
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>

                    <Divider sx={{ my: 1.5, borderColor: 'grey.300' }} />

                    {/* Metadatos con Iconos */}
                    <Stack spacing={1.2} sx={{ fontSize: '0.85rem' }}>
                      {/* Referente */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon sx={{ fontSize: 16, color: 'grey.400', flexShrink: 0 }} />
                        <Typography variant="body2" color="text.secondary" noWrap>
                          Referente: <span style={{ color: "#000", fontWeight: 550 }}>
                            {nota.referente}
                          </span>
                        </Typography>
                      </Box>

                      {/* Cursos + Coordinador */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SchoolIcon sx={{ fontSize: 16, color: 'grey.400', flexShrink: 0 }} />
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {nota.datos.cursos?.length || 0} curso(s) · Coord: <span style={{ color: "#000", fontWeight: 550 }}>{nota.coordNombre}</span>
                        </Typography>
                      </Box>

                      {/* Nota ID */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AssignmentIcon sx={{ fontSize: 16, color: 'grey.400', flexShrink: 0 }} />
                        <Typography variant="body2" color="text.secondary">
                          Nota ID: <span style={{ color: "#000", fontWeight: 550 }}>{nota.id}</span>
                        </Typography>
                      </Box>

                      {/* Fecha de Generación */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarTodayIcon sx={{ fontSize: 16, color: 'grey.400', flexShrink: 0 }} />
                        <Typography variant="body2" color="text.secondary">
                          Fecha de Generación: <span style={{ color: "#000", fontWeight: 550 }}>{formatDate(nota.fecha)}</span>
                        </Typography>
                      </Box>

                      {/* Fecha de Suba */}
                      {(nota.estado === 'PREV' || nota.estado === 'PEND' || nota.estado === 'AUT' || nota.estado === 'REC') && nota.fechaSuba && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CloudUploadIcon sx={{ fontSize: 16, color: 'grey.400', flexShrink: 0 }} />
                          <Typography variant="body2" color="text.secondary">
                            Fecha de Suba: <span style={{ color: "#000", fontWeight: 550 }}>{formatDate(nota.fechaSuba)}</span>
                          </Typography>
                        </Box>
                      )}

                      {/* Fecha de Autorización */}
                      {nota.estado === 'AUT' && nota.fechaAutorizacion && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main', flexShrink: 0 }} />
                          <Typography variant="body2" color="text.secondary">
                            Fecha de Autorización: <span style={{ color: "#000", fontWeight: 550 }}>{formatDate(nota.fechaAutorizacion)}</span>
                          </Typography>
                        </Box>
                      )}

                      {/* Fecha de Rechazo */}
                      {nota.estado === 'REC' && nota.fechaRechazo && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CancelIcon sx={{ fontSize: 16, color: 'error.main', flexShrink: 0 }} />
                          <Typography variant="body2" color="text.secondary">
                            Fecha de Rechazo: <span style={{ color: "#000", fontWeight: 550 }}>{formatDate(nota.fechaRechazo)}</span>
                          </Typography>
                        </Box>
                      )}
                    </Stack>

                    {/* Motivo de rechazo (solo si la nota está rechazada) */}
                    {nota.estado === 'REC' && nota.motivoRechazo && (
                      <Alert
                        severity="error"
                        icon={<ErrorOutlineIcon fontSize="small" />}
                        sx={{
                          mt: 2,
                          py: 0.5,
                          px: 1.5,
                          fontSize: '0.78rem',
                          borderRadius: 2,
                          bgcolor: 'rgba(226, 70, 76, 0.08)',
                          color: 'error.dark',
                          border: '1px solid',
                          borderColor: 'rgba(226, 70, 76, 0.25)',
                        }}
                      >
                        {nota.motivoRechazo}
                      </Alert>
                    )}
                  </CardContent>

                  {/* Acciones de la Tarjeta */}
                  <CardActions sx={{ px: 2.5, pb: 2.5, pt: 1 }}>
                    {nota.estado === 'PCARGA' ? (
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, width: '100%' }}>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                          onClick={() => setReviewingNota(nota)}
                          sx={{
                            fontSize: '0.78rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            py: 0.8,
                            borderRadius: 2,
                          }}
                        >
                          Ver Detalle
                        </Button>

                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                          onClick={() => setEditingNotaId(nota.id)}
                          sx={{
                            fontSize: '0.78rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            py: 0.8,
                            borderRadius: 2,
                            borderColor: 'grey.300',
                            color: 'text.primary',
                            '&:hover': {
                              borderColor: 'primary.main',
                              bgcolor: 'grey.50',
                            },
                          }}
                        >
                          Editar
                        </Button>

                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                          onClick={() => handleDeleteNota(nota.id)}
                          sx={{
                            fontSize: '0.78rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            py: 0.8,
                            borderRadius: 2,
                          }}
                        >
                          Eliminar
                        </Button>
                      </Box>
                    ) : (
                      <Button
                        variant={nota.estado === 'PREV' || nota.estado === 'PEND' ? 'contained' : 'outlined'}
                        color="primary"
                        size="small"
                        fullWidth
                        startIcon={<VisibilityIcon sx={{ fontSize: 18 }} />}
                        onClick={() => setReviewingNota(nota)}
                        sx={{
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          textTransform: 'none',
                          py: 0.85,
                          borderRadius: 2,
                        }}
                      >
                        {nota.estado === 'PREV' || nota.estado === 'PEND' ? 'Revisar Nota' : 'Ver Detalle'}
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Container>
  );
};

export default ConfirmarNotaAutorizacionAdmin;
